'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ShoppingCart, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import ProductImageFallback from '@/components/ui/ProductImageFallback';

// WhatsApp SVG icon
function WhatsAppIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

const CART_STORAGE_KEY = 'plciraq_quote_cart';

interface CartItem {
  mpn: string;
  brand?: string;
  description?: string;
  quantity: number;
}

interface Brand {
  name: string;
}

interface Product {
  id: string;
  mpn: string;
  description_en: string | null;
  stock_status: string | null;
  image_url: string | null;
  brands: Brand | null;
}

interface FeaturedProductsProps {
  language: 'en' | 'ar';
  onQuoteOpen: (partNumber?: string) => void;
}

function StockBadge({ status, language }: { status: string | null; language: 'en' | 'ar' }) {
  if (status === 'in_stock') {
    return (
      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-iraqi-green/15 text-iraqi-green border border-iraqi-green/30">
        {language === 'ar' ? '✅ متوفر' : '🟢 In Stock'}
      </span>
    );
  }
  if (status === 'backorder') {
    return (
      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
        {language === 'ar' ? 'على الطلب' : 'Backorder'}
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
      {language === 'ar' ? 'على الطلب' : 'On Request'}
    </span>
  );
}

function addToCart(item: CartItem): void {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const cart: CartItem[] = stored ? JSON.parse(stored) : [];
    const existing = cart.findIndex((c) => c.mpn === item.mpn);
    if (existing >= 0) {
      cart[existing].quantity += 1;
    } else {
      cart.push(item);
    }
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (err) {
    console.error('[FeaturedProducts] Failed to save to cart:', err);
  }
}

function getWhatsAppUrl(mpn: string, description: string): string {
  const message = `Hello PLC-IRAQ, I would like to request a quote for the following product:\n\nProduct: ${description}\nPart Number: ${mpn}\n\nPlease provide pricing and availability.`;
  return `https://wa.me/9647731885522?text=${encodeURIComponent(message)}`;
}

// Skeleton card for loading state
function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden animate-pulse">
      <div className="h-44 bg-slate-100" />
      <div className="p-5 space-y-3">
        <div className="h-3 bg-slate-100 rounded w-1/3" />
        <div className="h-4 bg-slate-100 rounded w-2/3" />
        <div className="h-3 bg-slate-100 rounded w-full" />
        <div className="h-10 bg-slate-100 rounded-lg mt-4" />
        <div className="h-10 bg-slate-100 rounded-lg" />
      </div>
    </div>
  );
}

export default function FeaturedProducts({ language }: FeaturedProductsProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedMpns, setAddedMpns] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function fetchFeaturedProducts() {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('products')
          .select('id, mpn, description_en, stock_status, image_url, brands(name)')
          .order('created_at', { ascending: false })
          .limit(4);

        if (error) {
          console.error('[FeaturedProducts] Supabase error:', error);
          return;
        }
        setProducts((data as Product[]) ?? []);
      } catch (err) {
        console.error('[FeaturedProducts] Unexpected error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchFeaturedProducts();
  }, []);

  const handleAddToQuote = (product: Product) => {
    addToCart({
      mpn: product.mpn,
      brand: product.brands?.name,
      description: product.description_en ?? product.mpn,
      quantity: 1,
    });
    setAddedMpns((prev) => new Set(prev).add(product.mpn));
    setTimeout(() => {
      setAddedMpns((prev) => {
        const next = new Set(prev);
        next.delete(product.mpn);
        return next;
      });
    }, 2000);
  };

  return (
    <section id="products" className="py-16 bg-light-grey" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl font-bold text-onyx font-space-grotesk mb-2">
              {language === 'ar' ? 'مكونات عالية الطلب' : 'High-Demand Components'}
            </h2>
            <p className="text-slate-grey font-roboto">
              {language === 'ar' ?'أكثر القطع طلباً من عملائنا في العراق' :'Most requested parts by our Iraqi clients'}
            </p>
          </div>
          <Link
            href="/products"
            className="hidden sm:flex items-center gap-1 text-iraqi-red text-sm font-semibold hover:underline font-space-grotesk"
          >
            {language === 'ar' ? 'عرض الكل' : 'View All'} →
          </Link>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            : products.map((product) => {
                const description = product.description_en ?? product.mpn;
                const whatsappUrl = getWhatsAppUrl(product.mpn, description);
                const isAdded = addedMpns.has(product.mpn);

                return (
                  <div
                    key={product.id}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5 group flex flex-col"
                  >
                    {/* Product Image */}
                    <Link
                      href={`/products/${encodeURIComponent(product.mpn)}`}
                      className="relative h-44 bg-light-grey overflow-hidden block"
                    >
                      <ProductImageFallback
                        src={product.image_url}
                        alt={`${product.mpn} — ${product.brands?.name ?? 'Industrial component'}`}
                        className="object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      />
                      {/* Stock badge overlay */}
                      <div className={`absolute top-3 ${language === 'ar' ? 'left-3' : 'right-3'}`}>
                        <StockBadge status={product.stock_status} language={language} />
                      </div>
                    </Link>

                    {/* Product Info */}
                    <div className="p-5 flex flex-col flex-1">
                      {/* Brand */}
                      {product.brands?.name && (
                        <span className="text-xs text-slate-grey font-roboto font-medium tracking-wide uppercase mb-1">
                          {product.brands.name}
                        </span>
                      )}
                      {/* MPN */}
                      <p className="text-xs text-slate-400 font-roboto mb-2 font-mono">{product.mpn}</p>
                      {/* Description */}
                      <p className="font-semibold text-onyx font-space-grotesk text-sm leading-snug mb-4 flex-1 line-clamp-2">
                        {description}
                      </p>

                      {/* Buttons */}
                      <div className="flex flex-col gap-2 mt-auto">
                        {/* Add to Quote Cart Button */}
                        <button
                          type="button"
                          onClick={() => handleAddToQuote(product)}
                          className={`w-full flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg transition-all duration-200 font-space-grotesk border ${
                            isAdded
                              ? 'bg-iraqi-green/10 text-iraqi-green border-iraqi-green/40' :'bg-white text-onyx border-slate-300 hover:border-iraqi-red hover:text-iraqi-red hover:bg-iraqi-red/5'
                          }`}
                          aria-label={`Add ${product.mpn} to quote cart`}
                        >
                          {isAdded ? (
                            <>
                              <CheckCircle size={14} />
                              {language === 'ar' ? 'تمت الإضافة' : 'Added to Cart'}
                            </>
                          ) : (
                            <>
                              <ShoppingCart size={14} />
                              {language === 'ar' ? 'أضف للعرض' : 'Add to Quote'}
                            </>
                          )}
                        </button>

                        {/* WhatsApp Quote Button */}
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-2 bg-iraqi-red hover:bg-primary-dark text-white text-sm font-semibold py-2.5 rounded-lg transition-all duration-200 hover:shadow-md hover:scale-[1.02] font-space-grotesk"
                          aria-label={`Request a quote via WhatsApp for ${product.mpn}`}
                          title="Chat on WhatsApp to request a quote"
                        >
                          <WhatsAppIcon size={14} />
                          {language === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Mobile View All link */}
        <div className="mt-8 text-center sm:hidden">
          <Link
            href="/products"
            className="inline-flex items-center gap-1 text-iraqi-red text-sm font-semibold hover:underline font-space-grotesk"
          >
            {language === 'ar' ? 'عرض الكل' : 'View All'} →
          </Link>
        </div>
      </div>
    </section>
  );
}
